import { createContext, useContext, useRef, useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { createPanZoomStore, type CanvasPanZoomStore } from './canvasPanZoomStore';
import { createDragStore, type CanvasDragStore } from './canvasDragStore';
import { createUiStore, type CanvasUiStore } from './canvasUiStore';
import type { CanvasStore } from './canvasStore';
import type { TransactionManager } from '../commands/transactionManager';
import type {
  ConnectionDragState,
  RedockDragState,
  CanvasLayer,
  CanvasSegment,
  CanvasGroup,
} from '../types';
import type { CanvasEntity, ConnectorData, GroupData } from '../entityTypes';
import { globalTasksStore } from '../../stores/globalTasksStore';
import type { GlobalTasksById } from '../../stores/types';
import { buildValidEntityIds } from '../lib/buildValidEntityIds';

// ============================================
// Shared Drag Ref Types
// ============================================

export interface RedockDragRefValue {
  state: RedockDragState;
  segmentId: string;
  end: 'from' | 'to';
  selectedEdgeIds: string[];
}

// ============================================
// Stable Context Value
// ============================================

export interface CanvasStoresValue {
  store: CanvasStore;
  txManager: TransactionManager;
  panZoomStore: CanvasPanZoomStore;
  dragStore: CanvasDragStore;
  uiStore: CanvasUiStore;
  boardId: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  connectionDragRef: React.RefObject<ConnectionDragState | null>;
  redockDragRef: React.RefObject<RedockDragRefValue | null>;
  measureMapRef: React.RefObject<Map<string, { width: number; height: number }>>;
  visibleEntityIdsRef: React.RefObject<Set<string>>;
  debugBatchRef: React.RefObject<{ batchSize: number; pending: number }>;
}

// ============================================
// Context
// ============================================

const CanvasStoresContext = createContext<CanvasStoresValue | null>(null);

// ============================================
// Provider
// ============================================

interface CanvasProviderProps {
  store: CanvasStore;
  txManager: TransactionManager;
  boardId: string;
  children: React.ReactNode;
}

export function CanvasProvider({ store, txManager, boardId, children }: CanvasProviderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const connectionDragRef = useRef<ConnectionDragState | null>(null);
  const redockDragRef = useRef<RedockDragRefValue | null>(null);
  const measureMapRef = useRef(new Map<string, { width: number; height: number }>());
  const visibleEntityIdsRef = useRef(new Set<string>());
  const debugBatchRef = useRef({ batchSize: 0, pending: 0 });

  const panZoomStore = useMemo(() => createPanZoomStore(), []);
  const dragStore = useMemo(() => createDragStore(), []);
  const uiStore = useMemo(() => createUiStore(), []);

  const value = useMemo<CanvasStoresValue>(() => ({
    store, txManager, panZoomStore, dragStore, uiStore, boardId, containerRef, connectionDragRef, redockDragRef, measureMapRef, visibleEntityIdsRef, debugBatchRef,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <CanvasStoresContext.Provider value={value}>
      {children}
    </CanvasStoresContext.Provider>
  );
}

// ============================================
// Core Hooks
// ============================================

export function useCanvasStores(): CanvasStoresValue {
  const ctx = useContext(CanvasStoresContext);
  if (!ctx) throw new Error('useCanvasStores must be used within CanvasProvider');
  return ctx;
}

export function useCanvasStore(): CanvasStore {
  return useCanvasStores().store;
}

export function useCanvasRotation(): number {
  const { store } = useCanvasStores();
  return useSelector(store, s => s.context.canvas.rotation ?? 0);
}

export function useCanvasLayers(): CanvasLayer[] {
  const { store } = useCanvasStores();
  return useSelector(store, s => s.context.layers);
}

export function useCanvasSidebarWidth(): number {
  const { uiStore } = useCanvasStores();
  return useSelector(uiStore, s => s.context.sidebarWidth);
}

export function useIsCanvasSidebarCollapsed(): boolean {
  const { uiStore } = useCanvasStores();
  return useSelector(uiStore, s => s.context.isSidebarCollapsed);
}

export function useIsCanvasSidebarResizing(): boolean {
  const { uiStore } = useCanvasStores();
  return useSelector(uiStore, s => s.context.isSidebarResizing);
}

// ============================================
// Materialization Helpers (segments / groups)
// ============================================

/** Filter raw node entities to valid entities. */
function filterEntities(rawNodes: CanvasEntity[], tasksById: GlobalTasksById): CanvasEntity[] {
  const validIds = buildValidEntityIds(rawNodes, tasksById);
  return rawNodes.filter(e => validIds.has(e.id));
}

export function materializeSegmentsFromStore(store: CanvasStore, tasksById: GlobalTasksById): CanvasSegment[] {
  const ctx = store.getSnapshot().context;

  const rawNodes = Object.values(ctx.nodes);
  const rawConnectors = Object.values(ctx.connectors);
  const validEntityIds = buildValidEntityIds(rawNodes, tasksById);

  const segments: CanvasSegment[] = [];
  for (const e of rawConnectors) {
    const cd = e.data as unknown as ConnectorData;
    if (!validEntityIds.has(cd.from.entityId) || !validEntityIds.has(cd.to.entityId)) continue;
    segments.push({
      id: e.id,
      from: cd.from,
      to: cd.to,
      segmentType: cd.segmentType,
      strokeColor: cd.strokeColor,
      strokeWidth: cd.strokeWidth,
      strokeStyle: cd.strokeStyle,
      arrowStart: cd.arrowStart,
      arrowEnd: cd.arrowEnd,
      label: cd.label,
      labelOffsetX: cd.labelOffsetX,
      labelOffsetY: cd.labelOffsetY,
    });
  }
  return segments;
}

export function materializeGroupsFromStore(store: CanvasStore, tasksById: GlobalTasksById): CanvasGroup[] {
  const ctx = store.getSnapshot().context;

  const rawNodes = Object.values(ctx.nodes);
  const rawGroups = Object.values(ctx.groups);
  const validEntityIds = buildValidEntityIds(rawNodes, tasksById);

  const groupIdSet = new Set(rawGroups.map(g => g.id));
  const validIds = new Set([...validEntityIds, ...groupIdSet]);

  return rawGroups.map(e => {
    const gd = e.data as unknown as GroupData;
    const memberNodeIds: string[] = [];
    const memberGroupIds: string[] = [];
    for (const mid of gd.memberEntityIds) {
      if (groupIdSet.has(mid)) memberGroupIds.push(mid);
      else memberNodeIds.push(mid);
    }
    return {
      id: e.id,
      label: gd.label,
      description: gd.description,
      backgroundColor: gd.backgroundColor,
      collapsed: gd.collapsed,
      collapsedX: gd.collapsedX,
      collapsedY: gd.collapsedY,
      memberEntityIds: gd.memberEntityIds.filter(id => validIds.has(id)),
      memberNodeIds: memberNodeIds.filter(id => validEntityIds.has(id)),
      memberGroupIds: memberGroupIds.filter(id => groupIdSet.has(id)),
      layerId: e.layerId,
      displayMode: gd.displayMode ?? 'invisible',
    };
  });
}

// ============================================
// Selector Hooks — stable via split maps
// ============================================

/**
 * Returns materialized segments, stable during entity drag.
 * Since connectors live in their own map, dragging nodes
 * doesn't change the connectors reference, so this hook doesn't re-render.
 */
export function useCanvasSegments(): CanvasSegment[] {
  const { store } = useCanvasStores();

  const connectors = useSelector(store, s => s.context.connectors);
  const nodesVersion = useSelector(store, s => s.context.nodesVersion);
  const tasksById = useSelector(globalTasksStore, s => s.context.tasksById);

  return useMemo(() => {
    const nodes = store.getSnapshot().context.nodes;
    const validEntityIds = buildValidEntityIds(Object.values(nodes), tasksById);
    const segments: CanvasSegment[] = [];
    for (const e of Object.values(connectors)) {
      const cd = e.data as unknown as ConnectorData;
      if (!validEntityIds.has(cd.from.entityId) || !validEntityIds.has(cd.to.entityId)) continue;
      segments.push({
        id: e.id,
        from: cd.from,
        to: cd.to,
        segmentType: cd.segmentType,
        strokeColor: cd.strokeColor,
        strokeWidth: cd.strokeWidth,
        strokeStyle: cd.strokeStyle,
        arrowStart: cd.arrowStart,
        arrowEnd: cd.arrowEnd,
        label: cd.label,
        labelOffsetX: cd.labelOffsetX,
        labelOffsetY: cd.labelOffsetY,
      });
    }
    return segments;
  }, [connectors, nodesVersion, tasksById, store]);
}

/**
 * Returns materialized groups, stable during entity drag.
 */
export function useCanvasGroups(): CanvasGroup[] {
  const { store } = useCanvasStores();

  const groups = useSelector(store, s => s.context.groups);
  const nodesVersion = useSelector(store, s => s.context.nodesVersion);
  const tasksById = useSelector(globalTasksStore, s => s.context.tasksById);

  return useMemo(() => {
    const nodes = store.getSnapshot().context.nodes;
    const validEntityIds = buildValidEntityIds(Object.values(nodes), tasksById);
    const groupValues = Object.values(groups);
    const groupIdSet = new Set(groupValues.map(g => g.id));
    const validIds = new Set([...validEntityIds, ...groupIdSet]);

    return groupValues.map(e => {
      const gd = e.data as unknown as GroupData;
      const memberNodeIds: string[] = [];
      const memberGroupIds: string[] = [];
      for (const mid of gd.memberEntityIds) {
        if (groupIdSet.has(mid)) memberGroupIds.push(mid);
        else memberNodeIds.push(mid);
      }
      return {
        id: e.id,
        label: gd.label,
        description: gd.description,
        backgroundColor: gd.backgroundColor,
        collapsed: gd.collapsed,
        collapsedX: gd.collapsedX,
        collapsedY: gd.collapsedY,
        memberEntityIds: gd.memberEntityIds.filter(id => validIds.has(id)),
        memberNodeIds: memberNodeIds.filter(id => validEntityIds.has(id)),
        memberGroupIds: memberGroupIds.filter(id => groupIdSet.has(id)),
        layerId: e.layerId,
        displayMode: gd.displayMode ?? 'invisible',
      };
    });
  }, [groups, nodesVersion, tasksById, store]);
}

/**
 * Returns filtered entities (tasks with valid globalTask, waypoints, textblocks, textlabels, sprites, etc.).
 * Excludes connectors and groups — they live in their own maps now.
 */
export function useCanvasEntities(): CanvasEntity[] {
  const { store } = useCanvasStores();
  const nodes = useSelector(store, s => s.context.nodes);
  const tasksById = useSelector(globalTasksStore, s => s.context.tasksById);

  return useMemo(
    () => filterEntities(Object.values(nodes), tasksById),
    [nodes, tasksById],
  );
}

/**
 * Returns IDs of top-level entities (not inside any group), in raw order.
 */
export function useCanvasTopLevelOrder(): string[] {
  const { store } = useCanvasStores();
  const nodesVersion = useSelector(store, s => s.context.nodesVersion);
  const groups = useSelector(store, s => s.context.groups);
  const tasksById = useSelector(globalTasksStore, s => s.context.tasksById);

  return useMemo(() => {
    const ctx = store.getSnapshot().context;
    const nodes = ctx.nodes;
    const groupsRec = ctx.groups;

    const validEntityIds = buildValidEntityIds(Object.values(nodes), tasksById);
    const groupValues = Object.values(groupsRec);
    const groupIdSet = new Set(groupValues.map(g => g.id));
    const validTopLevelIds = new Set([...validEntityIds, ...groupIdSet]);

    const allMemberIds = new Set<string>();
    for (const g of groupValues) {
      const gd = g.data as unknown as GroupData;
      for (const mid of gd.memberEntityIds) allMemberIds.add(mid);
    }

    const order: string[] = [];
    for (const e of Object.values(nodes)) {
      if (!validTopLevelIds.has(e.id)) continue;
      if (allMemberIds.has(e.id)) continue;
      order.push(e.id);
    }
    for (const e of groupValues) {
      if (!validTopLevelIds.has(e.id)) continue;
      if (allMemberIds.has(e.id)) continue;
      order.push(e.id);
    }
    return order;
  }, [nodesVersion, groups, tasksById, store]);
}
